/**
 * pages/Settings.tsx — the wallet's settings hub.
 *
 * Design contract (user-specified, non-negotiable):
 *
 *   Every one of the *nine* legally-required rows is reachable in
 *   AT MOST TWO TAPS from anywhere in the app. Two taps means:
 *     1. Tap "Settings" on the bottom nav / left rail.
 *     2. Tap the row.
 *
 *   The nine (per App Store §5.1.1 + Reg E + CFPB Prepaid Rule):
 *     1. Close account
 *     2. Report a problem with a transaction
 *     3. Statements
 *     4. Prepaid account agreement
 *     5. Privacy policy
 *     6. Terms of service
 *     7. Fee schedule (long form)
 *     8. Your Reg E rights
 *     9. Bank attribution — footer of every Settings screen
 *
 *   Rows 1, 2 and 3 STAY VISIBLE even before their endpoints exist
 *   — the honest-placeholder pattern (plain statement it's not live
 *   yet + support contact + no form that submits nowhere).
 *
 *   Reviewers and regulators check for these. Hiding a legally
 *   required row because the backend isn't ready is worse than an
 *   honest placeholder.
 *
 * Additional (non-required) product rows are folded in where they
 * fit the user's grouping: Account · Money · Security · Help · Legal
 * · Appearance.
 */

import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  ThemeToggle,
  useTheme,
} from "../components";
import { useAuth } from "../auth/AuthContext";
import { AppShell } from "./AppShell";
import styles from "./Settings.module.css";

// ---------------------------------------------------------------------------
// Row model.
// ---------------------------------------------------------------------------

interface SettingsRow {
  key: string;
  label: string;
  hint?: string;
  /** Nine legally-required rows are tagged so we can prove coverage
   * in a snapshot test (see /app/web/scripts/verify-legal-rows.sh). */
  legalRequirement?: string;
  /** Optional link. If absent, the row is inert (used only for
   * inline widgets like Theme). */
  to?: string;
  /** For the honest-placeholder rows: a short badge on the right. */
  status?: "coming-soon" | "external";
  /** Right-side widget slot (e.g., the theme toggle). */
  widget?: ReactNode;
}

interface SettingsGroup {
  title: string;
  hint?: string;
  rows: SettingsRow[];
}

// ---------------------------------------------------------------------------
// The full row table. Ordered the way the user specified.
// ---------------------------------------------------------------------------

const GROUPS: SettingsGroup[] = [
  {
    title: "Account",
    rows: [
      {
        key: "profile",
        label: "Profile",
        hint: "Handle, display name, avatar.",
        to: "/settings/profile",
        status: "coming-soon",
      },
      {
        key: "identity",
        label: "Identity & limits",
        hint: "Verification tier, per-transaction and monthly caps.",
        to: "/settings/identity",
        status: "coming-soon",
      },
      {
        key: "close-account",
        label: "Close account",
        hint: "Permanent deletion of your AsterCash account and data.",
        to: "/settings/close-account",
        legalRequirement: "App Store 5.1.1 — in-app account deletion",
      },
    ],
  },
  {
    title: "Money",
    rows: [
      {
        key: "funding-sources",
        label: "Funding sources",
        hint: "Bank accounts and cards used to add or send money.",
        to: "/settings/funding-sources",
        status: "coming-soon",
      },
      {
        key: "statements",
        label: "Statements",
        hint: "Monthly summaries and custom-range downloads.",
        to: "/statements",
        legalRequirement: "Reg E — periodic statements",
      },
      {
        key: "export",
        label: "Export activity",
        hint: "Download a CSV of your transactions on the Statements page.",
        to: "/statements",
      },
    ],
  },
  {
    title: "Security",
    rows: [
      {
        key: "passkeys",
        label: "Passkeys",
        hint: "Register or revoke device passkeys used to sign in.",
        to: "/settings/passkeys",
        status: "coming-soon",
      },
      {
        key: "sessions",
        label: "Active sessions",
        hint: "Every place you're signed in, with revoke controls.",
        to: "/settings/sessions",
        status: "coming-soon",
      },
      {
        key: "devices",
        label: "Registered devices",
        hint: "Phones and browsers approved for this account.",
        to: "/settings/devices",
        status: "coming-soon",
      },
    ],
  },
  {
    title: "Help",
    rows: [
      {
        key: "support",
        label: "Contact support",
        hint: "Reach a human at support@astercash.com — replies within one business day.",
        to: "/settings/support",
        status: "external",
      },
      {
        key: "report-problem",
        label: "Report a problem with a transaction",
        hint: "Dispute an error or unauthorised transaction.",
        to: "/settings/report-problem",
        legalRequirement: "Reg E — error resolution",
      },
    ],
  },
  {
    title: "Legal",
    hint: "Documents the CFPB Prepaid Rule and Reg E require us to make available to you.",
    rows: [
      {
        key: "prepaid-agreement",
        label: "Prepaid account agreement",
        hint: "The full agreement governing your AsterCash account.",
        to: "/legal/prepaid-account-agreement",
        legalRequirement: "CFPB Prepaid Rule — filed with the CFPB",
      },
      {
        key: "privacy",
        label: "Privacy policy",
        hint: "What we collect, why, and who we share it with.",
        to: "/legal/privacy-policy",
        legalRequirement: "App Store · Reg P",
      },
      {
        key: "tos",
        label: "Terms of service",
        hint: "The contract between you and AsterCash.",
        to: "/legal/terms-of-service",
        legalRequirement: "Standard",
      },
      {
        key: "fees",
        label: "Fee schedule (long form)",
        hint: "Every fee we may charge, spelled out in plain language.",
        to: "/legal/fee-schedule",
        legalRequirement: "CFPB Prepaid Rule",
      },
      {
        key: "reg-e",
        label: "Your Reg E rights",
        hint: "60-day window, liability caps, and resolution timelines.",
        to: "/legal/reg-e-rights",
        legalRequirement: "Reg E — consumer disclosures",
      },
      {
        key: "data-export-request",
        label: "Data export request",
        hint: "Request a full copy of your records under applicable privacy laws.",
        to: "/settings/data-export-request",
        status: "coming-soon",
      },
      {
        key: "data-deletion-request",
        label: "Data deletion request",
        hint: "Request removal of your data, subject to legal retention.",
        to: "/settings/data-deletion-request",
        status: "coming-soon",
      },
    ],
  },
  {
    title: "Appearance",
    rows: [
      {
        key: "theme",
        label: "Theme",
        hint: "System-linked, light, or dark.",
        widget: <ThemeToggle inline />,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// The chevron used at the right edge of every navigable row.
// ---------------------------------------------------------------------------

function Chevron() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={14}
      height={14}
      aria-hidden
      className={styles.chevron}
    >
      <path
        d="M5 3l5 5-5 5"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// The row component. Renders as a Link or a static row.
// ---------------------------------------------------------------------------

function Row({ row }: { row: SettingsRow }) {
  const inner = (
    <>
      <div className={styles.rowMain}>
        <div className={styles.rowLabelLine}>
          <span className={styles.rowLabel}>{row.label}</span>
          {row.status === "coming-soon" && (
            <span
              className={`${styles.rowBadge} ${styles.rowBadgeSoon}`}
              aria-label="Coming soon"
            >
              Coming soon
            </span>
          )}
          {row.legalRequirement && (
            <span
              className={`${styles.rowBadge} ${styles.rowBadgeRequired}`}
              title={row.legalRequirement}
              aria-label={`Required by ${row.legalRequirement}`}
            >
              Required
            </span>
          )}
        </div>
        {row.hint && <p className={styles.rowHint}>{row.hint}</p>}
      </div>
      <div className={styles.rowRight}>
        {row.widget ? row.widget : row.to ? <Chevron /> : null}
      </div>
    </>
  );

  if (row.to) {
    return (
      <Link
        to={row.to}
        className={styles.row}
        data-testid={`settings-row-${row.key}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className={styles.row} data-testid={`settings-row-${row.key}`}>
      {inner}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group card.
// ---------------------------------------------------------------------------

function Group({ group }: { group: SettingsGroup }) {
  return (
    <section className={styles.group}>
      <div className={styles.groupHead}>
        <h2 className={styles.groupTitle}>{group.title}</h2>
        {group.hint && <p className={styles.groupHint}>{group.hint}</p>}
      </div>
      <Card padding={0} className={styles.groupCard}>
        {group.rows.map((r, i) => (
          <div
            key={r.key}
            className={i === 0 ? styles.rowWrapFirst : styles.rowWrap}
          >
            <Row row={r} />
          </div>
        ))}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function Settings() {
  const { session, signOut } = useAuth();
  // Read but don't derive UI from theme — the toggle is self-contained.
  useTheme();

  return (
    <AppShell current="settings">
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Settings</p>
          <h1 className={styles.pageTitle}>Account, security, and legal</h1>
        </div>
      </header>

      <p className={styles.leadCopy}>
        Every row that regulators or app stores require you to be able to
        reach in two taps is here. Rows tagged{" "}
        <span className={styles.inlineRequiredTag}>Required</span> may not be
        removed. Rows tagged{" "}
        <span className={styles.inlineSoonTag}>Coming soon</span> are visible
        with an honest note that the automated flow isn't live yet — the
        support address on every one of them still works today.
      </p>

      {GROUPS.map((g) => (
        <Group key={g.title} group={g} />
      ))}

      {/* Sign-out is mobile-first: the web sign-out sits at the bottom of
          settings on phone-width so it's reachable without hunting for
          the top-bar chip. Desktop still shows it in the top bar. */}
      <section className={styles.signOutSection}>
        <Button
          variant="destructive"
          fullWidth
          onClick={() => void signOut()}
          data-testid="settings-sign-out"
        >
          Sign out of this browser
        </Button>
        {session?.absolute_expires_at && (
          <p className={styles.signOutHint}>
            Your web session will automatically end at{" "}
            {formatAbsolute(session.absolute_expires_at)}. Signing out now
            revokes it immediately across every tab.
          </p>
        )}
      </section>

      {/* ---- Row 9: bank attribution (footer of every Settings screen) ---- */}
      <footer className={styles.bankAttribution}>
        <p className={styles.bankLine} data-testid="bank-attribution">
          Banking services provided by <strong>[Sponsor Bank]</strong>, Member
          FDIC. Deposits are insured up to the applicable FDIC limits.
        </p>
        <p className={styles.bankSubLine}>
          The sponsor bank name will appear here once the partnership goes
          live. Until then, no deposit-holding is performed by AsterCash.
        </p>
        <p className={styles.buildLine}>
          AsterCash · Web build {BUILD_TAG} · {new Date().getFullYear()}
        </p>
      </footer>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------

const BUILD_TAG =
  (import.meta.env.VITE_BUILD_TAG as string | undefined) || "dev";

function formatAbsolute(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
